import { useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';
import Link from 'next/link';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import type { SimpleAggregation } from '@core/Entities/Search/Aggregations/Aggregations.entity';
import type { FilterKey } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { Typography } from '@gds/Typography/Typography';
import { useGetRoot } from '@search/Hooks/UseGetRoot';
import { fetchAggregationsWithActiveFilters } from '@search/Utils/FetchAggregationsWithActiveFilters';
import { useFilterLink } from '@search-filters/Hooks/UseFilterLink/UseFilterLink';

import styles from './QuickLinks.module.css';

type QuickLinksProps = {
  filterBaseId: string;
  locale: Locale;
  links: string[];
  strings: Record<string, string>;
  tenant?: Tenant;
};

export const QuickLinks = ({ filterBaseId, links, strings, tenant }: QuickLinksProps) => {
  const root = useGetRoot();
  const filtersList = useAtomValue(filtersListAtom);
  const [optsWithStock, setOptsWithStock] = useState<SimpleAggregation>({});
  const filterId = `${filterBaseId}__gte` as FilterKey;
  const partnerFilterId = `${filterBaseId}__lte` as FilterKey;

  const { getLinkForFilterValue } = useFilterLink({
    filterId,
    partnerFilterId,
    isPlpFiltersSeoEnabled: true,
    tenant,
  });

  useEffect(() => {
    const fetchAggregations = async () => {
      const otherFilters = filtersList.filter(filter => !filter.id.startsWith(filterBaseId));
      const aggs = await fetchAggregationsWithActiveFilters(otherFilters, root, undefined, tenant);
      setOptsWithStock((aggs[filterBaseId as keyof typeof aggs] as SimpleAggregation) ?? {});
    };

    fetchAggregations();
  }, [filtersList, filterBaseId, root, tenant]);

  return (
    <section className={styles.wrpr} data-test-id={`quick-links-${filterBaseId}`}>
      <Typography className={styles.title} variant="subheading3">
        {strings.quickLinks}
      </Typography>
      <div className={styles.linksCont}>
        {links.map((option: string) => {
          const link = getLinkForFilterValue({
            filterValue: option,
            resultCount: optsWithStock[option] ?? 0,
            isOnlyOption: links.length === 1,
          });

          return link?.href ? (
            <Link key={option} href={link.href} rel={link.nofollow ? 'nofollow' : undefined}>
              {option}
            </Link>
          ) : (
            <span key={option}>{option}</span>
          );
        })}
      </div>
    </section>
  );
};
